import { z } from "zod";
import {
  Box,
  EditableTitleField,
  WidgetInteractive,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";

/**
 * `section-header` — a transparent, headerless row that titles the widgets
 * below it. The board's one piece of structure that is not a figure: a
 * full-width heading with a hairline under it, so a long board reads in
 * chapters ("Pipeline", "Team", "This week"). The heading is edited in place
 * — click the text, type, Enter — so the module carries no settings sheet and
 * no Maximize: there is nothing behind it to open. The menu stays for
 * duplicate and delete.
 */
export const sectionHeaderConfigSchema = z.looseObject({
  title: z.string().default("Section"),
});

export type SectionHeaderConfig = z.infer<typeof sectionHeaderConfigSchema>;

function SectionHeaderRender({ config, onConfigChange }: WidgetRenderContext<SectionHeaderConfig>) {
  // `onConfigChange` IS the edit-rights signal (the note reads it the same
  // way): the host wires it only for a viewer who may edit this board.
  const editable = !!onConfigChange;
  return (
    <Box direction="column" fill="flex" justify="center">
      <Box direction="row" border="bottom" paddingBottom="xs">
        {/* The row is `bodyDraggable`; the title is its one interactive
            island, so a press on the text starts an edit, not a drag. */}
        <WidgetInteractive>
          <EditableTitleField
            size="xl"
            weight="semibold"
            value={config.title}
            editable={editable}
            onCommit={(next) => onConfigChange?.({ title: next })}
            placeholder="Section"
            aria-label="Section heading"
          />
        </WidgetInteractive>
      </Box>
    </Box>
  );
}

export const sectionHeaderDef: WidgetModule<SectionHeaderConfig> = {
  type: "section-header",
  title: "Section header",
  icon: "heading-1",
  chromeless: true,
  chrome: "headerless",
  chromeActions: { maximize: false, settings: false },
  span: { minW: 2, minH: 1, maxW: 12, maxH: 2, defaultW: 12, defaultH: 1 },
  configSchema: sectionHeaderConfigSchema,
  // The whole row is the grab surface; the heading text is the one island.
  bodyDraggable: true,
  catalog: [
    {
      key: "section-header.default",
      label: "Section header",
      description: "A transparent heading that titles the widgets below it.",
      section: "custom" satisfies WidgetSection,
      defaultConfig: { title: "Section" },
      defaultW: 12,
    },
  ],
  render: SectionHeaderRender,
};
